import React, { useState, useEffect } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import "chartjs-adapter-date-fns";
import { Text, Box, Flex } from "@chakra-ui/react";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const DataBalances = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const apiUrl = "https://blindfoldcapital.com/api/balances/";

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(apiUrl);
        if (!response.ok) throw new Error("Network response was not ok");
        const result = await response.json();
        setData(result.data);
      } catch (error) {
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) return <p>Loading...</p>;
  if (error) return <p>Error: {error}</p>;

  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString("en-US", {
      year: "numeric",
      month: "numeric",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
      hour12: true,
    });
  };

  const labels = [...new Set(data.map((entry) => formatDate(entry.timestamp)))];

  const usdtEntries = data.filter((entry) => entry.token === "USDT");
  const usdcEntries = data.filter((entry) => entry.token === "USDC");
  const usdcEEntries = data.filter((entry) => entry.token === "USDC.e");
  const wethEntries = data.filter((entry) => entry.token === "WETH");
  const wbtcEntries = data.filter((entry) => entry.token === "WBTC");
  const accountTotal = data.filter((entry) => entry.token === "ACCOUNT");

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        type: "category",
        ticks: {
          font: {
            size: 10,
          },
        },
      },
      y: {
        beginAtZero: true,
        ticks: {
          callback: function (value) {
            return value.toFixed(2);
          },
        },
      },
    },
  };

  const createChartData = (tokenData, tokenLabel, color) => ({
    labels,
    datasets: [
      {
        label: tokenLabel,
        data: tokenData.map((entry) => entry.balance),
        fill: false,
        borderColor: color,
        tension: 0.1,
      },
    ],
  });

  const renderChart = (entries, label, color) => {
    if (entries.length === 0 || entries[entries.length - 1].balance === 0) {
      return null;
    }

    const latest = entries[entries.length - 1];
    return (
      <Box w={{ base: "100%", md: "33.33%" }} px={4} mb={{ base: 6, md: 0 }} mx="auto">

        <Text fontSize="sm" fontWeight="bold" mb={2}>
          {label} Balance
        </Text>
        <Text fontSize="sm"  mb={2}>
          {latest.balance.toFixed(2)} {label} (as of {formatDate(latest.timestamp)})
        </Text>
        <Box height="300px" >
          <Line data={createChartData(entries, label, color)} options={chartOptions} />
        </Box>
      </Box>
    );
  };

  return (
    <Flex direction={{ base: "column", md: "row" }} wrap="wrap" justify="center">
      {renderChart(usdcEntries, "USDC", "blue")}
      {renderChart(usdtEntries, "USDT", "red")}
      {renderChart(usdcEEntries, "USDC.e", "green")}
      {renderChart(wethEntries, "WETH", "silver")}
      {renderChart(wbtcEntries, "WBTC", "orange")}
      {renderChart(accountTotal, "Account Total", "blue")}
    </Flex>
  );
};

export default DataBalances;
